import _ from 'lodash';
import TasksUpdated from './TasksUpdated';
import TasksIdsUpdated from './TasksIdsUpdated';
import TasksResourcesIdsUpdated from './TasksResourcesIdsUpdated';
import TasksDeleted from './TasksDeleted';
import store from '$$globalStore';
import TasksCreated from './TasksCreated';
import LinksCreated from './LinksCreated';
import LinksUpdated from './LinksUpdated';
import LinksDeleted from './LinksDeleted';
import LinksIdsUpdated from './LinksIdsUpdated';
import ProjectTasksUpdated from './ProjectTasksUpdated';

/*
  * Event format
  * {
  *  action: 'create | update | delete',
  *  entity: 'task' | 'project' | 'link',
  *  data: {}
  *  id,
  * }
*/
export default {
  eventsFromStore(mutation, ganttInstance) {
    let updatedTaskIdsForWorkload = null;
    let updatedResourceIdsForWorkload = null;

    if (mutation.type === 'tasksModel/addTasks') {
      TasksCreated.fromStore(mutation.payload.tasks, ganttInstance);
    }

    if (mutation.type === 'tasksModel/updateTasks') {
      TasksUpdated.fromStore(mutation.payload.tasks, ganttInstance);
      updatedTaskIdsForWorkload = mutation.payload.tasks.map(t => t.id);
    }

    if (mutation.type === 'tasksModel/updateTasksIds') {
      TasksIdsUpdated.fromStore(mutation.payload.createdTasks, ganttInstance);
    }

    if (mutation.type === 'tasksModel/deleteTasks') {
      TasksDeleted.fromStore(mutation.payload.tasks, ganttInstance);
      updatedTaskIdsForWorkload = mutation.payload.tasks.map(t => t.id);
    }

    if (mutation.type === 'tasksModel/addLinks') {
      LinksCreated.fromStore(mutation.payload.links, ganttInstance);
    }

    if (mutation.type === 'tasksModel/updateLinks') {
      LinksUpdated.fromStore(mutation.payload.links, ganttInstance);
    }

    if (mutation.type === 'tasksModel/updateLinksIds') {
      LinksIdsUpdated.fromStore(mutation.payload.createdLinks, ganttInstance);
    }

    if (mutation.type === 'tasksModel/removeLinks') {
      LinksDeleted.fromStore(mutation.payload.links, ganttInstance);
    }

    if (mutation.type === 'tasksModel/updateResourcesToTasks') {
      ganttInstance.render();
      updatedResourceIdsForWorkload = [];

      mutation.payload.resourcesToTasks.forEach(o => {
        updatedResourceIdsForWorkload = [...updatedResourceIdsForWorkload, ...o.resourcesToTask.map(r => r.resource_id)];
      });
    }

    if (mutation.type === 'tasksModel/updateResourcesToTasksIds') {
      TasksResourcesIdsUpdated.fromStore(mutation.payload.resourcesToTasks, ganttInstance);
    }

    if (mutation.type === 'tasksModel/updateProjectTasksData') {
      ProjectTasksUpdated.fromStore(mutation.payload, ganttInstance);
    }

    if (mutation.type === 'tasksModel/deleteTasksByGanttId') {
      ganttInstance.callEvent('workloadClearAndParse');
    }

    if (updatedTaskIdsForWorkload || updatedResourceIdsForWorkload) {
      ganttInstance.callEvent('workloadClearAndParse', [{
        taskIds: updatedTaskIdsForWorkload,
        resourceIds: updatedResourceIdsForWorkload,
      }]);
    }
  },
  async eventsFromGantt(unsavedChanges, ganttInstance) {
    const tasksToUpdate = [];
    const tasksToDelete = [];
    const tasksToCreate = [];
    const linksToUpdate = [];
    const linksToDelete = [];
    const linksToCreate = [];
    const storeTasksMap = store.getters['tasksModel/tasksByIds'];
    const checkFields = ['start_date', 'end_date', 'duration', 'progress', 'parent'];
    let needCheckProjectTasks = false;
    let needAutoschedule = false;

    unsavedChanges.task.forEach(unsavedTask => {
      const storeTask = storeTasksMap[unsavedTask.data.id];

      if (!storeTask) {
        needCheckProjectTasks = true;

        return;
      }

      needCheckProjectTasks = _.some(checkFields, field => {
        if (field === 'parent' && unsavedTask.data.parent === 1) {
          return false;
        }

        return !_.isEqual(unsavedTask.data[field], storeTask[field]);
      });

      if (!needAutoschedule && unsavedTask.action !== 'delete') {
        const constraintsGroup = ganttInstance.getConnectedGroup(unsavedTask.data.id);

        if (constraintsGroup.links.length) {
          needAutoschedule = true;
        }
      }
    });

    if (needCheckProjectTasks) {
      ganttInstance.eachTask(ganttTask => {
        if (ganttTask.type !== ganttInstance.config.types.project) {
          return;
        }

        if (ganttTask.id === 1) {
          return; // for multiViews
        }

        const storeTask = storeTasksMap[ganttTask.id];

        const isTaskChanged = _.some(checkFields, field => !_.isEqual(ganttTask[field], storeTask[field]));

        if (storeTask && isTaskChanged) {
          unsavedChanges.task.push({
            action: 'update',
            data: ganttTask,
          });
        }
      });
    }

    unsavedChanges.task.forEach(unsavedTask => {
      if (unsavedTask.action === 'create') {
        tasksToCreate.push(unsavedTask.data);
      }

      if (unsavedTask.action === 'update') {
        tasksToUpdate.push(unsavedTask.data);
      }

      if (unsavedTask.action === 'delete') {
        tasksToDelete.push(unsavedTask.data);
      }
    });
    unsavedChanges.link.forEach(unsavedLink => {
      if (unsavedLink.action === 'create') {
        linksToCreate.push(unsavedLink.data);
      }

      if (unsavedLink.action === 'update') {
        linksToUpdate.push(unsavedLink.data);
      }

      if (unsavedLink.action === 'delete') {
        linksToDelete.push(unsavedLink.data);
      }
    });

    if (tasksToCreate.length) {
      await store.dispatch('tasksModel/createTasks', {
        tasks: tasksToCreate,
        sourceEvent: unsavedChanges.sourceEvent,
        fromGantt: true,
        multiviewId: unsavedChanges.multiviewId,
        actionHash: unsavedChanges.actionHash,
      });
    }

    if (linksToCreate.length) {
      await store.dispatch('tasksModel/createLinks', {
        links: linksToCreate,
        sourceEvent: unsavedChanges.sourceEvent,
        fromGantt: true,
        multiviewId: unsavedChanges.multiviewId,
        actionHash: unsavedChanges.actionHash,
      });
    }

    if (linksToUpdate.length) { // update links before tasks to avoid collaboration loops
      await store.dispatch('tasksModel/updateLinks', {
        links: linksToUpdate,
        sourceEvent: unsavedChanges.sourceEvent,
        fromGantt: true,
        multiviewId: unsavedChanges.multiviewId,
        actionHash: unsavedChanges.actionHash,
      });
    }

    if (tasksToUpdate.length) {
      store.dispatch('tasksModel/updateTasks', {
        tasks: tasksToUpdate,
        sourceEvent: unsavedChanges.sourceEvent,
        fromGantt: true,
        multiviewId: unsavedChanges.multiviewId,
        actionHash: unsavedChanges.actionHash,
      });
    }

    if (tasksToDelete.length) {
      store.dispatch('tasksModel/deleteTasks', {
        tasks: tasksToDelete,
        sourceEvent: unsavedChanges.sourceEvent,
        fromGantt: true,
        multiviewId: unsavedChanges.multiviewId,
        actionHash: unsavedChanges.actionHash,
      });
    }

    if (linksToDelete.length) {
      store.dispatch('tasksModel/deleteLinks', {
        links: linksToDelete,
        sourceEvent: unsavedChanges.sourceEvent,
        fromGantt: true,
        multiviewId: unsavedChanges.multiviewId,
        actionHash: unsavedChanges.actionHash,
      });
    }

    if (tasksToUpdate.length || tasksToDelete.length) {
      let taskIds = [];

      if (tasksToUpdate.length) {
        taskIds = taskIds.concat(tasksToUpdate.map(o => o.id));
      }

      if (tasksToDelete.length) {
        taskIds = taskIds.concat(tasksToDelete.map(o => o.id));
      }

      if (ganttInstance.config.auto_scheduling.enabled && needAutoschedule) {
        // old logic from prod
        setTimeout(() => {
          if (ganttInstance.$destroyed) {
            return;
          }

          ganttInstance.eachTask(task => {
            if (task.type !== ganttInstance.config.types.button && task.constraint_date) {
              task.constraint_date = null;
              task.constraint_type = null;
            }
          });
          ganttInstance.autoSchedule(); // need next tick
          ganttInstance.render();
        }, 200);
      } else {
        ganttInstance.refreshData();
      }

      ganttInstance.callEvent('workloadClearAndParse', [{ taskIds }]);
    }
  },
};
